import { useMemo, useState } from "react";

import { useGetIdentity, useNotification, useTranslation } from "@refinedev/core";

import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  Link,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";

import { VerificationCodeInput } from "@/components/auth";
import { CardButton } from "@/components/common";
import {
  EmailMfaNoPhoneWarning,
  MfaEmailSetup,
  MfaSmsSetup,
  MfaTotpSetup,
  ProfileMfaMethodCard,
} from "@/components/mfa";
import { AUTHY_URL, GOOGLE_AUTHENTICATOR_URL, MICROSOFT_AUTHENTICATOR_URL } from "@/components/mfa/mfa-constants";
import { type MfaMethodType, useMfaResource } from "@/hooks/useMfaResource";
import { MfaMethod } from "@/types/types";
import type { IUser, UserMfaSettings } from "@/types/types";

import { faComment, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { faMobile } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Breadcrumb } from "./Breadcrumb";

/**
 * Profile MFA Management Panel
 * Allows users to enable/disable SMS, Authenticator App, and Email MFA methods
 * and manage their preferred MFA method.
 */
export const ChangeMFAPanel: React.FC = () => {
  const { translate } = useTranslation();
  const { open: openNotification } = useNotification();
  const { data: user, refetch: refetchIdentity } = useGetIdentity<IUser>();

  // MFA operations from hook
  const mfaResource = useMfaResource();
  const { sendCode, enableMfa, disableMfa, setPreferredMethod, sendEmailVerificationCode } = mfaResource;

  // Derive mfaData from user identity
  const mfaData = useMemo<UserMfaSettings | null>(
    () =>
      user?.mfa
        ? {
            phone_number: user.mfa.phone_number,
            phoneVerified: user.mfa.phoneVerified ?? false,
            smsEnabled: user.mfa.smsEnabled,
            status: user.mfa.status,
            totpEnabled: user.mfa.totpEnabled,
            emailEnabled: user.mfa.emailEnabled ?? false,
          }
        : null,
    [user]
  );

  // Local state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [setAsPreferred, setSetAsPreferred] = useState(false);
  const [expandedMethod, setExpandedMethod] = useState<MfaMethodType | null>(null);
  const [acknowledgedNoPhone, setAcknowledgedNoPhone] = useState(false);

  // Derive preferred method (fallback to first configured if NONE)
  const preferredMfaMethod: MfaMethod = useMemo(() => {
    const raw = mfaData?.status ?? MfaMethod.NONE;
    if (raw === MfaMethod.NONE) {
      if (mfaData?.smsEnabled) {
        return MfaMethod.SMS_MFA;
      }
      if (mfaData?.totpEnabled) {
        return MfaMethod.SOFTWARE_TOKEN_MFA;
      }
      if (mfaData?.emailEnabled) {
        return MfaMethod.EMAIL_OTP;
      }
    }
    return raw;
  }, [mfaData]);

  // Build preferred method dropdown options
  const preferredOptions = useMemo(() => {
    const opts: { key: MfaMethod; label: string }[] = [];
    if (mfaData?.smsEnabled) {
      opts.push({ key: MfaMethod.SMS_MFA, label: translate("pages.profile.mfa.smsTitle") });
    }
    if (mfaData?.totpEnabled) {
      opts.push({ key: MfaMethod.SOFTWARE_TOKEN_MFA, label: translate("pages.profile.mfa.totpTitle") });
    }
    if (mfaData?.emailEnabled) {
      opts.push({ key: MfaMethod.EMAIL_OTP, label: translate("pages.profile.mfa.emailTitle") });
    }
    return opts;
  }, [mfaData, translate]);

  // Handler: Change preferred method via dropdown
  const handlePreferredChange = async (event: SelectChangeEvent) => {
    const method = event.target.value as MfaMethodType;
    setIsSubmitting(true);
    const result = await setPreferredMethod(method);
    setIsSubmitting(false);

    if (result.success) {
      openNotification?.({
        type: "success",
        message: translate("pages.profile.mfa.success.preferredSet"),
      });
      await refetchIdentity();
    } else {
      openNotification?.({
        type: "error",
        message: result.error || translate("pages.profile.mfa.errors.setPreferredFailed"),
      });
    }
  };

  // Handler: Send SMS code
  const handleSendSmsCode = async () => {
    if (!phoneNumber.trim()) {
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);

    const result = await sendCode(MfaMethod.SMS_MFA, phoneNumber);
    setIsSubmitting(false);

    if (result.success) {
      setCodeSent(true);
    } else {
      const errorText = result.error?.includes("valid phone number")
        ? translate("pages.profile.mfa.errors.validPhoneNumber")
        : result.error || translate("pages.profile.mfa.errors.sendCodeFailed");
      setErrorMessage(errorText);
    }
  };

  // Handler: Accordion click - show/hide enable/disable forms
  const handleMethodClick = async (method: MfaMethodType) => {
    const isExpanded = expandedMethod === method;

    // Toggle: collapse if already expanded, expand if collapsed
    setExpandedMethod(isExpanded ? null : method);

    // Reset form state when collapsing
    if (isExpanded) {
      setVerificationCode("");
      setErrorMessage("");
      setPhoneNumber("");
      setCodeSent(false);
      setSetAsPreferred(false);
      setAcknowledgedNoPhone(false);
      return;
    }

    // Check if method is enabled
    const isEnabled =
      method === MfaMethod.SMS_MFA
        ? (mfaData?.smsEnabled ?? false)
        : method === MfaMethod.SOFTWARE_TOKEN_MFA
          ? (mfaData?.totpEnabled ?? false)
          : (mfaData?.emailEnabled ?? false);

    if (isEnabled && method === MfaMethod.SOFTWARE_TOKEN_MFA) {
      // Send code for TOTP disable flow
      setIsSubmitting(true);
      const result = await sendCode(method);
      setIsSubmitting(false);

      if (!result.success) {
        setErrorMessage(result.error || translate("pages.profile.mfa.errors.sendCodeFailed"));
      }
    } else if (!isEnabled && method === MfaMethod.SOFTWARE_TOKEN_MFA) {
      // For unconfigured TOTP, fetch secret immediately
      setIsSubmitting(true);
      const result = await sendCode(MfaMethod.SOFTWARE_TOKEN_MFA);
      setIsSubmitting(false);

      if (result.success && result.secretCode) {
        setTotpSecret(result.secretCode);
      } else {
        setErrorMessage(result.error || translate("pages.profile.mfa.errors.sendCodeFailed"));
      }
    }
  };

  // Helper: Get method display name
  const getMethodDisplayName = (method: MfaMethod) => {
    if (method === MfaMethod.SMS_MFA) {
      return translate("pages.profile.mfa.smsTitle");
    }
    if (method === MfaMethod.SOFTWARE_TOKEN_MFA) {
      return translate("pages.profile.mfa.totpTitle");
    }
    if (method === MfaMethod.EMAIL_OTP) {
      return translate("pages.profile.mfa.emailTitle");
    }
    return translate("pages.profile.mfa.none");
  };

  // Render: List View
  const renderListView = () => {
    const smsEnabled = mfaData?.smsEnabled ?? false;
    const totpEnabled = mfaData?.totpEnabled ?? false;
    const emailEnabled = mfaData?.emailEnabled ?? false;

    return (
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={3}>
            {/* Preferred Method - Dropdown when methods are configured */}
            {preferredOptions.length >= 1 ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="subtitle1" color="primary.dark">
                  {translate("pages.profile.mfa.preferredMethod")}:
                </Typography>
                <FormControl size="small">
                  <Select
                    value={preferredMfaMethod}
                    onChange={handlePreferredChange}
                    disabled={isSubmitting || preferredOptions.length <= 1}
                  >
                    {preferredOptions.map(opt => (
                      <MenuItem key={opt.key} value={opt.key}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" color="primary.dark">
                  {translate("pages.profile.mfa.preferredMethod")}:
                </Typography>
                <Typography variant="subtitle1" color="text.primary">
                  {getMethodDisplayName(preferredMfaMethod)}
                </Typography>
              </Stack>
            )}

            {/* Description */}
            <Typography variant="body2" color="text.disabled">
              {translate("pages.profile.mfa.description")}
            </Typography>

            {/* Methods Title */}
            <Typography variant="body1" color="text.primary">
              {translate("pages.profile.mfa.methodsTitle")}
            </Typography>

            {/* TOTP Method */}
            <ProfileMfaMethodCard
              method={MfaMethod.SOFTWARE_TOKEN_MFA}
              icon={<FontAwesomeIcon icon={faMobile} fontSize={20} />}
              title={translate("pages.profile.mfa.totpTitle")}
              description={
                <>
                  {translate("pages.profile.mfa.totpDescription")}
                  <Typography variant="caption" component="span" display="block" mt={1}>
                    {translate("pages.mfa.totpTry")}{" "}
                    <Link
                      href={GOOGLE_AUTHENTICATOR_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      underline="always"
                      color="inherit"
                    >
                      {translate("pages.mfa.googleAuthenticator")}
                    </Link>
                    {", "}
                    <Link
                      href={MICROSOFT_AUTHENTICATOR_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      underline="always"
                      color="inherit"
                    >
                      {translate("pages.mfa.microsoftAuthenticator")}
                    </Link>
                    {", "}
                    <Link
                      href={AUTHY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      underline="always"
                      color="inherit"
                    >
                      {translate("pages.mfa.authy")}
                    </Link>
                  </Typography>
                </>
              }
              statusBadge={totpEnabled ? "configured" : "not-configured"}
              onClick={handleMethodClick}
              isExpanded={expandedMethod === MfaMethod.SOFTWARE_TOKEN_MFA}
              securityTag={{ label: translate("pages.mfa.mostSecure"), variant: "success" }}
            />

            {/* SMS Method */}
            <ProfileMfaMethodCard
              method={MfaMethod.SMS_MFA}
              icon={<FontAwesomeIcon icon={faComment} fontSize={20} />}
              title={translate("pages.profile.mfa.smsTitle")}
              description={translate("pages.profile.mfa.smsDescription")}
              statusBadge={smsEnabled ? "configured" : "not-configured"}
              onClick={handleMethodClick}
              isExpanded={expandedMethod === MfaMethod.SMS_MFA}
              securityTag={{ label: translate("pages.mfa.lessSecure"), variant: "error" }}
            />

            {/* Email Method */}
            <ProfileMfaMethodCard
              method={MfaMethod.EMAIL_OTP}
              icon={<FontAwesomeIcon icon={faEnvelope} fontSize={20} />}
              title={translate("pages.profile.mfa.emailTitle")}
              description={translate("pages.profile.mfa.emailDescription")}
              statusBadge={emailEnabled ? "configured" : "not-configured"}
              onClick={handleMethodClick}
              isExpanded={expandedMethod === MfaMethod.EMAIL_OTP}
              securityTag={{ label: translate("pages.mfa.lessSecure"), variant: "error" }}
              extraContent={
                expandedMethod === MfaMethod.EMAIL_OTP && !emailEnabled && !mfaData?.phoneVerified ? (
                  <EmailMfaNoPhoneWarning
                    acknowledgedNoPhone={acknowledgedNoPhone}
                    onAcknowledgeChange={setAcknowledgedNoPhone}
                  />
                ) : undefined
              }
            />

            {/* Divider between cards and expanded content */}
            {expandedMethod && <Divider />}

            {/* SMS Forms */}
            {expandedMethod === MfaMethod.SMS_MFA &&
              (smsEnabled ? (
                // Disable form - requires SEND CODE first
                <Stack spacing={2}>
                  <Typography variant="h6" color="primary">
                    {translate("pages.profile.mfa.disableSmsTitle")}
                  </Typography>
                  <Typography variant="body2" color="text.hint">
                    {translate("pages.profile.mfa.sendCodeDescription")}
                  </Typography>
                  <CardButton
                    onClick={async () => {
                      setErrorMessage("");
                      setIsSubmitting(true);
                      const result = await sendCode(MfaMethod.SMS_MFA, mfaData?.phone_number || "");
                      setIsSubmitting(false);
                      if (result.success) {
                        setCodeSent(true);
                      } else {
                        setErrorMessage(result.error || translate("pages.profile.mfa.errors.sendCodeFailed"));
                      }
                    }}
                    disabled={isSubmitting || codeSent}
                    fullWidth
                  >
                    {translate(isSubmitting ? "pages.mfa.sending" : "pages.mfa.sendCode")}
                  </CardButton>

                  {/* Only show verification section after code is sent */}
                  {codeSent && (
                    <>
                      <Typography variant="body2" color="text.hint">
                        {translate("pages.mfa.enterCodeInstructions")}
                      </Typography>
                      <VerificationCodeInput
                        value={verificationCode}
                        onChange={code => {
                          setVerificationCode(code);
                          setErrorMessage("");
                        }}
                        disabled={isSubmitting}
                      />
                      {errorMessage && (
                        <Typography variant="body2" color="error.dark" align="center">
                          {errorMessage}
                        </Typography>
                      )}
                      <CardButton
                        onClick={async () => {
                          if (verificationCode.length !== 6) {
                            return;
                          }
                          setIsSubmitting(true);
                          const result = await disableMfa(MfaMethod.SMS_MFA, verificationCode);
                          setIsSubmitting(false);
                          if (result.success) {
                            openNotification?.({
                              type: "success",
                              message: translate("pages.profile.mfa.success.disabled", {
                                method: getMethodDisplayName(MfaMethod.SMS_MFA),
                              }),
                            });
                            await refetchIdentity();
                            setExpandedMethod(null);
                            setVerificationCode("");
                            setCodeSent(false);
                          } else {
                            setErrorMessage(result.error || translate("pages.profile.mfa.errors.disableFailed"));
                          }
                        }}
                        disabled={verificationCode.length !== 6 || isSubmitting}
                        fullWidth
                      >
                        {translate(isSubmitting ? "pages.mfa.verifying" : "pages.profile.mfa.disableSmsButton")}
                      </CardButton>
                    </>
                  )}

                  {/* Show error message outside of codeSent block if it occurs during send */}
                  {!codeSent && errorMessage && (
                    <Typography variant="body2" color="error.dark" align="center">
                      {errorMessage}
                    </Typography>
                  )}
                </Stack>
              ) : (
                // Enable form
                <MfaSmsSetup
                  phoneNumber={phoneNumber}
                  onPhoneNumberChange={value => {
                    setPhoneNumber(value);
                    setErrorMessage("");
                  }}
                  verificationCode={verificationCode}
                  onCodeChange={code => {
                    setVerificationCode(code);
                    setErrorMessage("");
                  }}
                  onSendCode={handleSendSmsCode}
                  showEmailRecoveryInfo
                  onVerify={async () => {
                    if (verificationCode.length !== 6) {
                      return;
                    }
                    setIsSubmitting(true);
                    const result = await enableMfa(
                      verificationCode,
                      MfaMethod.SMS_MFA,
                      setAsPreferred ? MfaMethod.SMS_MFA : mfaData?.status || MfaMethod.NONE
                    );
                    if (result.success) {
                      openNotification?.({
                        type: "success",
                        message: translate("pages.profile.mfa.success.enabled", {
                          method: getMethodDisplayName(MfaMethod.SMS_MFA),
                        }),
                      });
                      await refetchIdentity();
                      setExpandedMethod(null);
                      setPhoneNumber("");
                      setVerificationCode("");
                      setCodeSent(false);
                      setSetAsPreferred(false);
                    } else {
                      setErrorMessage(result.error || translate("pages.profile.mfa.errors.invalidCode"));
                    }
                    setIsSubmitting(false);
                  }}
                  isSubmitting={isSubmitting}
                  codeSent={codeSent}
                  errorMessage={errorMessage}
                  showSetPreferred={true}
                  setAsPreferred={setAsPreferred}
                  onSetPreferredChange={setSetAsPreferred}
                  submitButtonLabel={translate("pages.profile.mfa.enableSmsButton")}
                />
              ))}

            {/* TOTP Forms */}
            {expandedMethod === MfaMethod.SOFTWARE_TOKEN_MFA &&
              (totpEnabled ? (
                // Disable form
                <Stack spacing={2}>
                  <Typography variant="h6" color="primary">
                    {translate("pages.profile.mfa.disableTitle")}
                  </Typography>
                  <Typography variant="body2" color="text.hint">
                    {translate("pages.mfa.enterCodeInstructions")}
                  </Typography>
                  <VerificationCodeInput
                    value={verificationCode}
                    onChange={code => {
                      setVerificationCode(code);
                      setErrorMessage("");
                    }}
                    disabled={isSubmitting}
                  />
                  {errorMessage && (
                    <Typography variant="body2" color="error.dark" align="center">
                      {errorMessage}
                    </Typography>
                  )}
                  <CardButton
                    onClick={async () => {
                      if (verificationCode.length !== 6) {
                        return;
                      }
                      setIsSubmitting(true);
                      const result = await disableMfa(MfaMethod.SOFTWARE_TOKEN_MFA, verificationCode);
                      setIsSubmitting(false);
                      if (result.success) {
                        openNotification?.({
                          type: "success",
                          message: translate("pages.profile.mfa.success.disabled", {
                            method: getMethodDisplayName(MfaMethod.SOFTWARE_TOKEN_MFA),
                          }),
                        });
                        await refetchIdentity();
                        setExpandedMethod(null);
                        setVerificationCode("");
                      } else {
                        setErrorMessage(result.error || translate("pages.profile.mfa.errors.disableFailed"));
                      }
                    }}
                    disabled={verificationCode.length !== 6 || isSubmitting}
                    fullWidth
                  >
                    {translate(isSubmitting ? "pages.mfa.verifying" : "pages.profile.mfa.disableButton")}
                  </CardButton>
                </Stack>
              ) : (
                // Enable form
                <MfaTotpSetup
                  totpSecret={totpSecret}
                  email={user?.email || ""}
                  verificationCode={verificationCode}
                  onCodeChange={code => {
                    setVerificationCode(code);
                    setErrorMessage("");
                  }}
                  onVerify={async () => {
                    if (verificationCode.length !== 6) {
                      return;
                    }
                    setIsSubmitting(true);
                    const result = await enableMfa(
                      verificationCode,
                      MfaMethod.SOFTWARE_TOKEN_MFA,
                      setAsPreferred ? MfaMethod.SOFTWARE_TOKEN_MFA : mfaData?.status || MfaMethod.NONE
                    );
                    if (result.success) {
                      openNotification?.({
                        type: "success",
                        message: translate("pages.profile.mfa.success.enabled", {
                          method: getMethodDisplayName(MfaMethod.SOFTWARE_TOKEN_MFA),
                        }),
                      });
                      await refetchIdentity();
                      setExpandedMethod(null);
                      setVerificationCode("");
                      setSetAsPreferred(false);
                    } else {
                      setErrorMessage(result.error || translate("pages.profile.mfa.errors.invalidCode"));
                    }
                    setIsSubmitting(false);
                  }}
                  isSubmitting={isSubmitting}
                  errorMessage={errorMessage}
                  showSetPreferred={true}
                  setAsPreferred={setAsPreferred}
                  onSetPreferredChange={setSetAsPreferred}
                  submitButtonLabel={translate("pages.profile.mfa.enableTotpButton")}
                />
              ))}

            {/* Email Forms */}
            {expandedMethod === MfaMethod.EMAIL_OTP &&
              (emailEnabled ? (
                // Disable form - requires email verification code
                <Stack spacing={2}>
                  <Typography variant="h6" color="primary">
                    {translate("pages.profile.mfa.disableEmailTitle")}
                  </Typography>
                  <Typography variant="body2" color="text.hint">
                    {translate("pages.profile.mfa.emailDisableDescription")}
                  </Typography>
                  <CardButton
                    onClick={async () => {
                      setErrorMessage("");
                      setIsSubmitting(true);
                      const result = await sendEmailVerificationCode();
                      setIsSubmitting(false);
                      if (result.success) {
                        setCodeSent(true);
                      } else {
                        setErrorMessage(result.error || translate("pages.profile.mfa.errors.emailCodeError"));
                      }
                    }}
                    disabled={isSubmitting || codeSent}
                    fullWidth
                  >
                    {translate(isSubmitting ? "pages.mfa.sending" : "pages.profile.mfa.sendEmailCode")}
                  </CardButton>

                  {codeSent && (
                    <>
                      <Typography variant="body2" color="text.hint">
                        {translate("pages.profile.mfa.emailCodeSent")}
                      </Typography>
                      <VerificationCodeInput
                        value={verificationCode}
                        onChange={code => {
                          setVerificationCode(code);
                          setErrorMessage("");
                        }}
                        disabled={isSubmitting}
                      />
                      {errorMessage && (
                        <Typography variant="body2" color="error.dark" align="center">
                          {errorMessage}
                        </Typography>
                      )}
                      <CardButton
                        onClick={async () => {
                          if (verificationCode.length !== 6) {
                            return;
                          }
                          setIsSubmitting(true);
                          const result = await disableMfa(MfaMethod.EMAIL_OTP, verificationCode);
                          setIsSubmitting(false);
                          if (result.success) {
                            openNotification?.({
                              type: "success",
                              message: translate("pages.profile.mfa.success.disabled", {
                                method: getMethodDisplayName(MfaMethod.EMAIL_OTP),
                              }),
                            });
                            await refetchIdentity();
                            setExpandedMethod(null);
                            setVerificationCode("");
                            setCodeSent(false);
                          } else {
                            setErrorMessage(result.error || translate("pages.profile.mfa.errors.disableFailed"));
                          }
                        }}
                        disabled={verificationCode.length !== 6 || isSubmitting}
                        fullWidth
                      >
                        {translate(isSubmitting ? "pages.mfa.verifying" : "pages.profile.mfa.disableEmailButton")}
                      </CardButton>
                    </>
                  )}

                  {!codeSent && errorMessage && (
                    <Typography variant="body2" color="error.dark" align="center">
                      {errorMessage}
                    </Typography>
                  )}
                </Stack>
              ) : (
                // Enable form
                <MfaEmailSetup
                  phoneVerified={mfaData?.phoneVerified ?? false}
                  existingPhoneNumber={mfaData?.phone_number ?? null}
                  acknowledgedNoPhone={acknowledgedNoPhone}
                  onEnable={async () => {
                    setIsSubmitting(true);
                    const noMethodsConfigured = !mfaData?.smsEnabled && !mfaData?.totpEnabled && !mfaData?.emailEnabled;
                    const result = await enableMfa(
                      undefined,
                      MfaMethod.EMAIL_OTP,
                      setAsPreferred
                        ? MfaMethod.EMAIL_OTP
                        : noMethodsConfigured
                          ? MfaMethod.EMAIL_OTP
                          : mfaData?.status || MfaMethod.NONE
                    );
                    if (result.success) {
                      openNotification?.({
                        type: "success",
                        message: translate("pages.profile.mfa.success.enabled", {
                          method: getMethodDisplayName(MfaMethod.EMAIL_OTP),
                        }),
                      });
                      await refetchIdentity();
                      setExpandedMethod(null);
                      setSetAsPreferred(false);
                    } else {
                      setErrorMessage(result.error || translate("pages.profile.mfa.errors.enableFailed"));
                    }
                    setIsSubmitting(false);
                  }}
                  isSubmitting={isSubmitting}
                  errorMessage={errorMessage}
                  showSetPreferred={true}
                  setAsPreferred={setAsPreferred}
                  onSetPreferredChange={setSetAsPreferred}
                  submitButtonLabel={translate("pages.profile.mfa.enableEmailButton")}
                />
              ))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Breadcrumb
        items={[
          { label: translate("pages.profile.sections.profileAndSecurity"), to: "/profile?section=profile-security" },
          { label: translate("pages.profile.options.changeMfa") },
        ]}
      />

      {renderListView()}
    </Box>
  );
};
